/* Vital Affiliate Wizard — Cart Watcher
 *
 * Globally intercepts cart-removal requests (fetch AND XHR). If a user tries to remove
 * the Welcome Pack while wizard-added PM-priced products are still in cart, we show a
 * confirmation:
 *   - "Keep pack" → cancel the removal silently
 *   - "Remove pack & clear cart" → wipe the cart (pack + addons go together)
 *
 * Intercepts: /cart/change.js, /cart/update.js, /cart/clear.js, and form POST to /cart.
 * Loaded on every page via theme.liquid.
 */
(function () {
  'use strict';

  const LOG = '[Wizard Cart Watcher]';

  // ---- Modal HTML (lazy-injected on first need) ----
  let modalEl = null;
  function ensureModal() {
    if (modalEl) return modalEl;
    const html = `
<div id="wizard-cart-guard" style="display:none;position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.6);align-items:center;justify-content:center;padding:20px;font-family:'Outfit',sans-serif;">
  <div style="background:#fff;border-radius:16px;max-width:520px;width:100%;padding:32px;box-shadow:0 20px 60px rgba(0,0,0,0.3);text-align:center;">
    <div style="width:64px;height:64px;border-radius:50%;background:#fef3c7;margin:0 auto 16px;display:flex;align-items:center;justify-content:center;font-size:32px;">⚠️</div>
    <h2 style="margin:0 0 12px;font-size:22px;font-weight:700;color:#111827;line-height:1.3;">Removing your Welcome Pack will clear your cart</h2>
    <p style="margin:0 0 24px;color:#4b5563;font-size:15px;line-height:1.55;">Preferred Member pricing on individual products requires keeping the Welcome Pack in your cart. Removing the pack will also remove those items.</p>
    <div style="display:flex;flex-direction:column;gap:10px;">
      <button type="button" data-wcg-keep style="padding:14px 24px;background:#53a43f;color:#fff;border:none;border-radius:8px;font-size:15px;font-weight:600;cursor:pointer;">Keep pack &amp; continue</button>
      <button type="button" data-wcg-remove style="padding:14px 24px;background:#fff;color:#dc2626;border:1.5px solid #dc2626;border-radius:8px;font-size:15px;font-weight:600;cursor:pointer;">Remove pack &amp; clear cart</button>
    </div>
  </div>
</div>`;
    document.body.insertAdjacentHTML('beforeend', html);
    modalEl = document.getElementById('wizard-cart-guard');
    return modalEl;
  }

  function showGuardModal() {
    return new Promise((resolve) => {
      const el = ensureModal();
      el.style.display = 'flex';
      const cleanup = () => {
        el.style.display = 'none';
        keepBtn.onclick = null;
        removeBtn.onclick = null;
        el.onclick = null;
      };
      const keepBtn = el.querySelector('[data-wcg-keep]');
      const removeBtn = el.querySelector('[data-wcg-remove]');
      keepBtn.onclick = () => { cleanup(); resolve('keep'); };
      removeBtn.onclick = () => { cleanup(); resolve('remove'); };
      el.onclick = (e) => { if (e.target === el) { cleanup(); resolve('keep'); } };
    });
  }

  // ---- Cart-shape helpers ----
  function isPackLine(line) {
    if (!line || !line.properties) return false;
    const p = line.properties;
    return !!(p._bundle_pack || p._bbg_builder === '1' || p._bbg_builder === 1 || p._bbg_builder === true);
  }
  function hasWizardAddons(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(li =>
      li.properties && (li.properties._wizard_addon === '1' || li.properties._wizard_addon === 'true' || li.properties._wizard_addon === true)
    );
  }

  // Cached cart (avoid extra fetches on each op)
  let cachedCart = null;
  let cachedAt = 0;
  async function getCart(force) {
    const now = Date.now();
    if (!force && cachedCart && (now - cachedAt) < 1500) return cachedCart;
    try {
      const r = await origFetch.call(window, '/cart.js', { credentials: 'same-origin' });
      cachedCart = await r.json();
      cachedAt = Date.now();
      return cachedCart;
    } catch (e) {
      return null;
    }
  }

  // ---- Body parsing helpers ----
  function parseRequestBody(body) {
    if (!body) return null;
    if (typeof body === 'string') {
      // Try JSON first
      try { return JSON.parse(body); } catch (e) {}
      // Try URL-encoded
      try {
        const params = new URLSearchParams(body);
        return paramsToObject(params);
      } catch (e) {}
      return null;
    }
    if (body instanceof FormData || body instanceof URLSearchParams) {
      return paramsToObject(body);
    }
    if (typeof body === 'object') return body;
    return null;
  }

  function paramsToObject(params) {
    // Convert form-encoded keys like `updates[KEY]=0` or `cart[updates][KEY]=0` into nested
    const out = {};
    const setNested = (obj, path, value) => {
      let cur = obj;
      for (let i = 0; i < path.length - 1; i++) {
        const k = path[i];
        if (!cur[k] || typeof cur[k] !== 'object') cur[k] = {};
        cur = cur[k];
      }
      cur[path[path.length - 1]] = value;
    };
    const parseKey = (key) => {
      const m = key.match(/^([^\[]+)((?:\[[^\]]*\])*)/);
      if (!m) return [key];
      const head = m[1];
      const tail = m[2];
      const segs = [head];
      const re = /\[([^\]]*)\]/g;
      let mm;
      while ((mm = re.exec(tail)) !== null) segs.push(mm[1]);
      return segs;
    };
    params.forEach((v, k) => {
      const segs = parseKey(k);
      setNested(out, segs, v);
    });
    return out;
  }

  // ---- Detect: does this cart op remove the pack line? ----
  async function detectsPackRemoval(url, body) {
    const cart = await getCart(false);
    if (!cart || !cart.items || cart.items.length === 0) return false;

    // /cart/clear wipes everything
    if (/\/cart\/clear(\.js)?(\?|$|#)/.test(url)) {
      return cart.items.some(isPackLine);
    }

    // /cart/change: { id|line, quantity }
    if (/\/cart\/change(\.js)?(\?|$|#)/.test(url) && body) {
      const qty = parseInt(body.quantity, 10);
      if (qty === 0) {
        let targetLine = null;
        if (body.id != null) targetLine = cart.items.find(li => String(li.key) === String(body.id) || String(li.variant_id) === String(body.id));
        else if (body.line != null) targetLine = cart.items[parseInt(body.line, 10) - 1];
        return !!(targetLine && isPackLine(targetLine));
      }
      return false;
    }

    // /cart/update.js OR /cart (form POST): { updates: {key: qty} | [qty, qty, ...] }
    // Form POST may nest as { cart: { updates: ... } } or be flat
    let updates = null;
    if (body) {
      if (body.updates) updates = body.updates;
      else if (body.cart && body.cart.updates) updates = body.cart.updates;
    }
    if (updates) {
      if (Array.isArray(updates)) {
        for (let i = 0; i < updates.length; i++) {
          if (parseInt(updates[i], 10) === 0) {
            const line = cart.items[i];
            if (line && isPackLine(line)) return true;
          }
        }
      } else if (typeof updates === 'object') {
        for (const k in updates) {
          if (parseInt(updates[k], 10) === 0) {
            const line = cart.items.find(li => String(li.key) === String(k) || String(li.variant_id) === String(k));
            if (line && isPackLine(line)) return true;
          }
        }
      }
    }
    return false;
  }

  function isCartOpUrl(url) {
    if (!url) return false;
    // .js endpoints
    if (/\/cart\/(change|update|clear)(\.js)?(\?|$|#|\/)/.test(url)) return true;
    // Plain /cart POST (cart page form submit). Match `/cart` or `/cart?...` or `/cart#...`
    // but NOT /cart.js or /cart/anything-else
    if (/\/cart(\?|#|$)/.test(url) && !/\/cart\.js/.test(url)) return true;
    return false;
  }

  function fakeCartResponse(cart) {
    return new Response(JSON.stringify(cart || { items: [], item_count: 0 }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  async function clearCart() {
    cachedCart = null;
    try {
      await origFetch.call(window, '/cart/clear.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
    } catch (e) { console.warn(LOG, 'clear failed', e); }
  }

  // Shared decision: should we block this op?
  async function shouldBlockAndConfirm(url, body) {
    // Wizard signals its own pack-swap operations via this flag so the guard doesn't
    // intercept the wizard's authorized pack-line clears (e.g., pack-A → pack-B swap).
    if (window.__wizardSwapInProgress) {
      console.log(LOG, 'wizard swap in progress, bypassing guard');
      return { block: false };
    }
    const wouldRemovePack = await detectsPackRemoval(url, body);
    if (!wouldRemovePack) return { block: false };
    const cart = await getCart(true);
    if (!hasWizardAddons(cart)) return { block: false };
    console.log(LOG, 'pack removal detected with addons in cart — showing modal');
    const choice = await showGuardModal();
    return { block: choice === 'keep', cleared: choice === 'remove' };
  }

  // When user clicks "Keep pack", we need to return a response shape that the theme
  // can use to re-render the cart UI without removing anything. The theme expects
  // `sections` (rendered HTML) in the response. We get those by doing a no-op cart
  // change (qty stays the same).
  async function buildKeepResponse(body, cart) {
    const sections = body && body.sections;
    if (Array.isArray(sections) && sections.length > 0 && cart && cart.items && cart.items.length > 0) {
      const anyLine = cart.items[0];
      try {
        const noOpRes = await origFetch.call(window, '/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: anyLine.key,
            quantity: anyLine.quantity, // no-op qty change
            sections: sections,
            sections_url: (body && body.sections_url) || '/cart'
          })
        });
        if (noOpRes.ok) {
          const text = await noOpRes.text();
          return new Response(text, { status: 200, headers: { 'Content-Type': 'application/json' } });
        }
      } catch (e) { console.warn(LOG, 'no-op fetch failed', e); }
    }
    return fakeCartResponse(cart);
  }

  // ---- Intercept fetch ----
  const origFetch = window.fetch.bind(window);
  window.fetch = async function patchedFetch(input, init) {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (isCartOpUrl(url)) {
        const body = parseRequestBody(init && init.body);
        console.log(LOG, 'fetch intercepted', url, body);
        const decision = await shouldBlockAndConfirm(url, body);
        if (decision.block) {
          const cart = await getCart(true);
          return buildKeepResponse(body, cart);
        }
        if (decision.cleared) {
          await clearCart();
          try { window.location.reload(); } catch (e) {}
          return fakeCartResponse({ items: [], item_count: 0 });
        }
      }
      const res = await origFetch.call(this, input, init);
      if (isCartOpUrl(url)) scheduleCartRefresh();
      return res;
    } catch (e) {
      console.warn(LOG, 'fetch interceptor error', e);
      return origFetch.call(this, input, init);
    }
  };

  // Keep cachedCart fresh after cart ops. Debounce so multiple rapid /cart/update.js calls
  // (e.g., bundle-sync's cascade) only trigger one re-fetch.
  let _refreshTimer = null;
  function scheduleCartRefresh() {
    clearTimeout(_refreshTimer);
    _refreshTimer = setTimeout(() => {
      _refreshTimer = null;
      getCart(true).catch(() => {});
    }, 250);
  }

  // ---- Intercept XHR (jQuery.ajax + legacy code uses XHR) ----
  const OrigXHR = window.XMLHttpRequest;
  function PatchedXHR() {
    const xhr = new OrigXHR();
    let interceptUrl = '';
    let interceptMethod = '';

    const origOpen = xhr.open;
    xhr.open = function (method, url) {
      interceptMethod = (method || '').toUpperCase();
      interceptUrl = url || '';
      return origOpen.apply(xhr, arguments);
    };

    const origSend = xhr.send;
    xhr.send = function (body) {
      if (interceptMethod === 'POST' && isCartOpUrl(interceptUrl)) {
        const parsed = parseRequestBody(body);
        console.log(LOG, 'XHR intercepted', interceptUrl, parsed);
        // Synchronously decide via async work — we need to PAUSE the send while we ask the user
        shouldBlockAndConfirm(interceptUrl, parsed).then((decision) => {
          if (decision.block) {
            // Fake a success response. Synthesize properties on the xhr.
            try {
              Object.defineProperty(xhr, 'readyState', { value: 4, configurable: true });
              Object.defineProperty(xhr, 'status', { value: 200, configurable: true });
              Object.defineProperty(xhr, 'responseText', { value: JSON.stringify(cachedCart || { items: [] }), configurable: true });
              if (typeof xhr.onreadystatechange === 'function') xhr.onreadystatechange();
              if (typeof xhr.onload === 'function') xhr.onload();
            } catch (e) { console.warn(LOG, 'XHR fake response failed', e); }
            return;
          }
          if (decision.cleared) {
            clearCart().then(() => {
              try { window.location.reload(); } catch (e) {}
            });
            return;
          }
          // No block — send through
          origSend.call(xhr, body);
        });
        return;
      }
      return origSend.apply(xhr, arguments);
    };

    return xhr;
  }
  PatchedXHR.prototype = OrigXHR.prototype;
  window.XMLHttpRequest = PatchedXHR;

  // ---- Pre-fetch cart on load so the click interceptor can decide synchronously ----
  // Event propagation is synchronous: if we await before calling stopImmediatePropagation,
  // the theme's listener has already fired. So we keep a fresh cart in cache and check sync.
  getCart(true).then((c) => {
    console.log(LOG, 'pre-fetch complete, cart items=' + ((c && c.items && c.items.length) || 0));
  }).catch((e) => console.warn(LOG, 'pre-fetch failed', e));
  // Also re-fetch on DOMContentLoaded in case script loaded before DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { getCart(true).catch(() => {}); });
  }

  // ---- Intercept clicks on remove links/buttons (SYNCHRONOUS path) ----
  function extractRemoveRef(target) {
    if (!target) return null;
    // href pattern: /cart/change?id=KEY&quantity=0 or ?line=N&quantity=0
    let href = target.getAttribute && target.getAttribute('href');
    if (!href && target.querySelector) {
      const innerA = target.querySelector('a');
      if (innerA) href = innerA.getAttribute('href');
    }
    if (href) {
      try {
        const u = new URL(href, window.location.origin);
        if (/\/cart\/change/.test(u.pathname)) {
          const qty = u.searchParams.get('quantity');
          const id = u.searchParams.get('id');
          const line = u.searchParams.get('line');
          if (qty === '0') {
            if (id) return { kind: 'id', value: id };
            if (line) return { kind: 'line', value: parseInt(line, 10) };
          }
        }
      } catch (e) {}
    }
    // Data attributes
    if (target.getAttribute) {
      const k = target.getAttribute('data-cart-remove') || target.getAttribute('data-line-key') || target.getAttribute('data-line-remove');
      if (k) return { kind: 'id', value: k };
    }
    // Class-based fallback already covered by the href check above (we look at inner <a>)
    return null;
  }

  document.addEventListener('click', (ev) => {
    try {
      const target = ev.target.closest('a, button, [data-cart-remove], [data-line-remove], cart-remove-button');
      if (!target) return;

      const removeRef = extractRemoveRef(target);
      if (!removeRef) return;

      console.log(LOG, 'remove click detected', { removeRef, hasCache: !!cachedCart, cacheItems: (cachedCart && cachedCart.items && cachedCart.items.length) || 0 });

      // SYNCHRONOUS cart check using cached cart. If cache missing/stale we let through
      // (fetch interceptor below will catch the actual cart change as backup).
      const cart = cachedCart;
      if (!cart || !cart.items || cart.items.length === 0) {
        console.warn(LOG, 'no cached cart — falling back to fetch interceptor (slight delay)');
        return;
      }

      let lineObj = null;
      if (removeRef.kind === 'id') {
        lineObj = cart.items.find(li => String(li.key) === String(removeRef.value) || String(li.variant_id) === String(removeRef.value));
      } else if (removeRef.kind === 'line') {
        const idx = removeRef.value - 1;
        if (idx >= 0 && idx < cart.items.length) lineObj = cart.items[idx];
      }
      console.log(LOG, 'line resolved', { lineObj: lineObj && { key: lineObj.key, props: lineObj.properties }, isPack: lineObj && isPackLine(lineObj), hasAddons: hasWizardAddons(cart) });

      if (!lineObj || !isPackLine(lineObj)) return;
      if (!hasWizardAddons(cart)) return;

      // It's a pack-remove with addons. Block SYNCHRONOUSLY so the theme's bubble-phase
      // listener never runs and updateQuantity never fires.
      console.log(LOG, 'pack-remove CLICK intercepted (sync block)', removeRef);
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();

      // Async: show modal + handle choice
      (async () => {
        const choice = await showGuardModal();
        if (choice === 'remove') {
          await clearCart();
          try { window.location.href = '/cart'; } catch (e) { window.location.reload(); }
        }
        // 'keep' = do nothing — cart was never touched, UI is already correct
      })();
    } catch (e) {
      console.warn(LOG, 'click interceptor error', e);
    }
  }, true);

  // Keep cachedCart fresh: re-fetch after page becomes visible (in case user changed cart elsewhere)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) getCart(true).catch(() => {});
  });

  console.log(LOG, 'installed (fetch + XHR + click)');
})();
